import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { Save } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { CreateOutNoteRequest } from '@/lib/api/packaging-lot-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { Combobox } from '@/components/ui/combobox'
import { packagingLotApi } from '@/lib/api/packaging-lot-api'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { useCreatePackagingOutNote } from '@/hooks/use-packaging-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { packagingLotOption } from '@/components/lots/lot-option-builders'
import { usePackagingLotSource } from '@/components/lots/lot-picker-source'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    kind: z.literal('existing'),
    packaging_lot_id: z
        .string({ required_error: m.form_err_pick_existing_lot() })
        .uuid(m.form_err_pick_existing_lot()),
    quantity: z.coerce
        .number({
            required_error: m.form_err_quantity_required(),
            invalid_type_error: m.form_err_number_invalid(),
        })
        .positive(m.form_err_quantity_positive()),
})

// Reason=for_order KHÔNG bắt buộc chọn đơn.
//
// Xưởng có hai kiểu xuất cho đơn: xuất theo một đơn cụ thể, và xuất GỘP cuối
// ngày sau khi thủ kho đếm lại túi — kiểu sau không móc vào đơn nào (#M-16, sổ
// kiểm kê túi). Bắt chọn đơn thì kiểu sau không lưu được phiếu.
const schema = z.object({
    reason: z.enum([
        'for_order',
        'discard',
        'adjustment',
        'for_packaging_process',
    ]),
    order_id: z.string().uuid().optional().or(z.literal('')),
    notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
    items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
})

type FormData = z.infer<typeof schema>
type OrderOpt = { value: string; label: string }

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-out-notes/new',
)({
    component: NewPXKPage,
})

function NewPXKPage() {
    const navigate = useNavigate()
    const create = useCreatePackagingOutNote()

    const handleSubmit = async (
        value: CreateOutNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        try {
            const res = await workflow.run(
                () => create.mutateAsync(value),
                (result) => result.data?.id,
            )
            const noteId = res.data?.id
            if (noteId) {
                void navigate({
                    to: '/packaging-warehouse/stock-out-notes/$id',
                    params: { id: noteId },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PackagingLotStockOutForm
            isPending={create.isPending}
            onSubmit={handleSubmit}
            onCancel={() =>
                navigate({
                    to: '/packaging-warehouse/stock-out-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function PackagingLotStockOutForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    seedOrderOptions,
    seedLotOptions,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    /** Seed đơn hàng hiện tại (khi sửa phiếu for_order) để combobox hiển thị. */
    seedOrderOptions?: OrderOpt[]
    /** Lô của phiếu đang sửa: phải hiện/chọn được dù đã cạn tồn hoặc nằm
     *  ngoài trang đã tải. */
    seedLotOptions?: LotOption[]
    isPending: boolean
    onSubmit: (
        value: CreateOutNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
}) {
    const workflow = useWorkflowTracking('packaging_stock_out', {
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const isEdit = mode === 'edit'
    // Chỉ lô còn tồn — tìm server + cuộn tải thêm (nguồn lô dùng chung).
    const lotSource = usePackagingLotSource({
        filters: { only_available: '1' },
        seed: seedLotOptions,
    })

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'for_order',
            order_id: '',
            notes: '',
            items: [{ kind: 'existing', packaging_lot_id: '', quantity: 0 }],
        },
    })

    const reason = form.watch('reason')
    const isForOrder = reason === 'for_order'

    // Reset order_id khi user đổi sang reason khác — tránh gửi order_id ngoài
    // ngữ cảnh. Khi SỬA, reason bị khoá nên không reset (giữ đơn của phiếu gốc).
    useEffect(() => {
        if (!isForOrder && !isEdit) form.setValue('order_id', '')
    }, [isForOrder, isEdit, form])

    const [orderSearch, setOrderSearch] = useState('')
    const debouncedOrderSearch = useDebounced(orderSearch)
    const { data: ordersData } = useSalesOrders(
        isForOrder
            ? {
                  per_page: 30,
                  ...(debouncedOrderSearch
                      ? { search: debouncedOrderSearch }
                      : {}),
              }
            : undefined,
    )
    // Đơn hàng đã chọn tay được cache để nhãn không rớt khỏi trigger khi search
    // server đổi options (server chỉ trả danh sách theo từ khoá hiện tại;
    // seedOrderOptions chỉ có đơn gốc của phiếu khi sửa).
    const [pickedOrders, setPickedOrders] = useState<Map<string, OrderOpt>>(
        new Map(),
    )
    const orderOptions = useMemo<OrderOpt[]>(() => {
        const map = new Map<string, OrderOpt>()
        for (const o of seedOrderOptions ?? []) map.set(o.value, o)
        for (const o of ordersData?.data ?? []) {
            map.set(o.id, {
                value: o.id,
                label: `${o.order_code} — ${o.customer_name}`,
            })
        }
        for (const [id, o] of pickedOrders) map.set(id, o)
        return Array.from(map.values())
    }, [ordersData, seedOrderOptions, pickedOrders])

    // Cache lot đã quét mã vạch — usePackagingLots top 50, lô scan có thể
    // ngoài trang đầu → Combobox không hiện tên.
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const injectedScannedLots = useMemo(
        () => Array.from(scannedLots.values()),
        [scannedLots],
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await packagingLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, packagingLotOption(l))
                return next
            })
        } catch {
            // ignore
        }
    }

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('_lot_id') || name === 'reason')
                workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const submit = form.handleSubmit(
        (data) => {
            const items = data.items.map((it) => ({
                packaging_lot_id: it.packaging_lot_id,
                quantity: Number(it.quantity),
            }))
            onSubmit(
                {
                    reason: data.reason,
                    notes: data.notes,
                    items,
                    ...(data.reason === 'for_order' && data.order_id
                        ? { order_id: data.order_id }
                        : {}),
                },
                workflow,
            )
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <PageBackButton onBack={onCancel} />
                    <h1 className="text-xl font-semibold">
                        {title ?? m.pkl_new_pxk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form onSubmit={submit} className="space-y-6 max-w-4xl">
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4">
                        <div>
                            <Label className="mb-1 block">
                                {m.sl_new_reason_out()}
                            </Label>
                            <Controller
                                control={form.control}
                                name="reason"
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        disabled={isEdit}
                                        onValueChange={field.onChange}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="for_order">
                                                {m.pkl_out_reason_for_sale()}
                                            </SelectItem>
                                            <SelectItem value="discard">
                                                {m.pkl_out_reason_discard()}
                                            </SelectItem>
                                            <SelectItem value="adjustment">
                                                {m.sl_new_reason_out_adjustment()}
                                            </SelectItem>
                                            {/* PXK sang quy trình đóng gói giờ được hệ
                                                thống tự sinh khi duyệt PNK thành phẩm có
                                                khai vật tư — chỉ hiện khi xem/sửa phiếu
                                                cũ còn mang lý do này. */}
                                            {form.watch('reason') ===
                                                'for_packaging_process' && (
                                                <SelectItem value="for_packaging_process">
                                                    {m.pxbb_v2_reason_for_packaging_process()}
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block">
                                {m.sl_new_notes()}
                            </Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                        {isForOrder && (
                            <div className="md:col-span-2">
                                <Label className="mb-1 block">
                                    {m.pkl_out_order_label()}
                                </Label>
                                <Controller
                                    control={form.control}
                                    name="order_id"
                                    render={({ field }) => (
                                        <Combobox
                                            options={orderOptions}
                                            value={field.value || undefined}
                                            onChange={(v) => {
                                                const opt = orderOptions.find(
                                                    (o) => o.value === v,
                                                )
                                                if (opt)
                                                    setPickedOrders((prev) => {
                                                        if (prev.has(v))
                                                            return prev
                                                        const next = new Map(
                                                            prev,
                                                        )
                                                        next.set(v, opt)
                                                        return next
                                                    })
                                                field.onChange(v)
                                            }}
                                            onSearchChange={setOrderSearch}
                                            onOpenChange={(open) => {
                                                if (!open) setOrderSearch('')
                                            }}
                                            disabled={isEdit}
                                            shouldFilter={false}
                                            placeholder={m.pkl_out_order_placeholder()}
                                            searchPlaceholder={m.pkl_out_order_search_placeholder()}
                                            emptyText={m.pkl_out_order_empty()}
                                        />
                                    )}
                                />
                                {form.formState.errors.order_id?.message && (
                                    <p className="text-sm text-destructive mt-1">
                                        {form.formState.errors.order_id.message}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <LotItemsField
                        mode="out"
                        lotSource={lotSource}
                        scannedLots={injectedScannedLots}
                        categoryOptions={[]}
                        categoryLabel={m.sl_lot_grade()}
                        existingIdField="packaging_lot_id"
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'packaging-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue(
                                        'items',
                                        [
                                            ...current,
                                            {
                                                kind: 'existing',
                                                packaging_lot_id: lotId,
                                                quantity: 0,
                                            },
                                        ],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        },
                                    )
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
